import React, { useState } from "react";
import { Sparkles, X, Info } from "lucide-react";

/**
 * DemoMaturityBanner
 * ------------------
 * A non-intrusive banner that only renders inside the LIVE DEMO tenant.
 * Explains that the workspace is pre-populated at ~80% operational maturity
 * so prospects don't mistake the rich data for coincidence.
 *
 * Renders nothing unless the current user/org is flagged as a demo tenant.
 */
export default function DemoMaturityBanner({ user }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.sessionStorage.getItem("gbrms_demo_maturity_dismissed") === "1";
    } catch {
      return false;
    }
  });

  const isDemo =
    user?.plan_type === "demo" ||
    user?.org_plan_type === "demo" ||
    user?.is_demo === true ||
    (typeof user?.email === "string" && user.email.toLowerCase().includes("gbrms-sandbox.com"));

  if (!isDemo || dismissed) return null;

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem("gbrms_demo_maturity_dismissed", "1");
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div
      data-testid="demo-maturity-banner"
      className="relative overflow-hidden rounded-xl border border-sky-200/70 bg-gradient-to-r from-sky-50 via-white to-indigo-50 px-3 py-3 sm:px-5 sm:py-3.5 shadow-sm"
    >
      <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-sky-100/60 blur-2xl" aria-hidden />
      <div className="relative flex items-start gap-2.5 sm:gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sky-700 text-white shadow">
          <Sparkles className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p
              className="text-[0.82rem] sm:text-sm font-bold text-slate-800 leading-tight"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Live Demo — pre-populated at ~80% operational maturity
            </p>
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-white/80 px-2 py-0.5 text-[0.6rem] font-semibold text-sky-700">
              <Info className="h-3 w-3" /> Demo
            </span>
          </div>
          <p className="mt-1 text-[0.75rem] sm:text-[0.8rem] leading-relaxed text-slate-600">
            You are exploring a lived-in ResiliencyHUB tenant — Asset Inventory, BIA, Risk Assessment,
            Recovery Plans, and the Exercise Calendar are already stocked with realistic, interdependent
            data so you can evaluate every workflow end-to-end. Any edits you make here stay isolated to
            this demo sandbox.
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.7rem] font-semibold text-slate-500">
            <span data-testid="demo-maturity-chip-assets" className="inline-flex items-center gap-2">
              48 Assets<span className="text-slate-300">•</span>
            </span>
            <span data-testid="demo-maturity-chip-bia" className="inline-flex items-center gap-2">
              22 BIAs · 77% approved<span className="text-slate-300">•</span>
            </span>
            <span data-testid="demo-maturity-chip-risks" className="inline-flex items-center gap-2">
              22 Risks · 77% treated<span className="text-slate-300">•</span>
            </span>
            <span data-testid="demo-maturity-chip-plans" className="inline-flex items-center gap-2">
              12 Recovery Plans · 75% approved<span className="text-slate-300">•</span>
            </span>
            <span data-testid="demo-maturity-chip-exercises">20 Exercises · Q1–Q4 2026</span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss demo maturity banner"
          data-testid="demo-maturity-banner-dismiss"
          className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
